/**
 * Session Manager Utility
 *
 * Manages user session timeout detection and keepalive functionality.
 * Prevents work loss by warning users before session expiration.
 */

const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
const WARNING_BEFORE_EXPIRY = 5 * 60 * 1000; // Warn 5 minutes before expiry
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every minute
const KEEPALIVE_INTERVAL = 10 * 60 * 1000; // Ping server every 10 minutes during activity

class SessionManager {
  constructor() {
    this.lastActivity = Date.now();
    this.sessionStart = Date.now();
    this.warningCallback = null;
    this.expiredCallback = null;
    this.sessionRestoredCallback = null;
    this.checkInterval = null;
    this.keepaliveInterval = null;
    this.isWarningShown = false;
    this.isSessionExpired = false;

    this.initializeActivityTracking();
    this.initializeSessionSyncListener();
    this.startSessionMonitoring();
  }

  /**
   * Track user activity to detect if user is actively working
   */
  initializeActivityTracking() {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const updateActivity = () => {
      this.lastActivity = Date.now();
      this.isWarningShown = false;
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
  }

  /**
   * Listen to DCACLabSessionSync for cross-tab session events
   * DCACLabSessionSync handles visibility changes and CSRF token refresh
   */
  initializeSessionSyncListener() {
    const setupListener = () => {
      if (window.DCACLabSessionSync) {
        window.DCACLabSessionSync.onSessionChange((eventType, userId) => {
          if (eventType === 'signin' || eventType === 'session_refreshed') {
            // Session was restored - reset state
            this.sessionStart = Date.now();
            this.isWarningShown = false;
            
            if (this.isSessionExpired) {
              this.isSessionExpired = false;
              if (this.sessionRestoredCallback) {
                this.sessionRestoredCallback({ userId });
              }
            }
          } else if (eventType === 'signout') {
            this.isSessionExpired = true;
          }
        });
        return true;
      }
      return false;
    };

    // Try immediately, or wait for DCACLabSessionSync to be available
    if (!setupListener()) {
      const checkInterval = setInterval(() => {
        if (setupListener()) {
          clearInterval(checkInterval);
        }
      }, 500);
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }

  /**
   * Start monitoring session timeout
   */
  startSessionMonitoring() {
    // Check session status every minute
    this.checkInterval = setInterval(() => {
      this.checkSessionStatus();
    }, ACTIVITY_CHECK_INTERVAL);

    // Send keepalive pings every 10 minutes if user is active
    this.keepaliveInterval = setInterval(() => {
      this.sendKeepalive();
    }, KEEPALIVE_INTERVAL);
  }

  /**
   * Check if session is about to expire or has expired
   */
  checkSessionStatus() {
    const now = Date.now();
    const sessionAge = now - this.sessionStart;
    const timeUntilExpiry = SESSION_DURATION - sessionAge;

    // Session has expired
    if (timeUntilExpiry <= 0) {
      this.handleSessionExpired();
      return;
    }

    // Session is about to expire - show warning
    if (timeUntilExpiry <= WARNING_BEFORE_EXPIRY && !this.isWarningShown) {
      this.handleWarning(timeUntilExpiry);
    }
  }

  /**
   * Send keepalive request to server to extend session
   */
  async sendKeepalive() {
    const timeSinceActivity = Date.now() - this.lastActivity;

    // Only send keepalive if user has been active in last 10 minutes
    if (timeSinceActivity < KEEPALIVE_INTERVAL) {
      try {
        const response = await fetch('/api/session/keepalive', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
          }
        });

        if (response.ok) {
          // Reset session start time on successful keepalive
          this.sessionStart = Date.now();
          this.isWarningShown = false;
          console.log('Session extended');
        } else if (response.status === 401) {
          // Session already expired
          this.handleSessionExpired();
        }
      } catch (error) {
        console.error('Keepalive request failed:', error);
      }
    }
  }

  /**
   * Manually extend session (called when user clicks "Stay logged in")
   */
  async extendSession() {
    try {
      const response = await fetch('/api/session/keepalive', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      });

      if (response.ok) {
        this.sessionStart = Date.now();
        this.isWarningShown = false;
        return true;
      } else if (response.status === 401) {
        this.handleSessionExpired();
        return false;
      }
    } catch (error) {
      console.error('Session extension failed:', error);
      return false;
    }
  }

  /**
   * Handle warning state
   */
  handleWarning(timeUntilExpiry) {
    this.isWarningShown = true;
    const minutesLeft = Math.ceil(timeUntilExpiry / 60000);

    if (this.warningCallback) {
      this.warningCallback(minutesLeft);
    }
  }

  /**
   * Handle session expiration
   */
  handleSessionExpired() {
    this.isSessionExpired = true;
    
    if (this.expiredCallback) {
      this.expiredCallback();
    }

    // Stop monitoring after expiration
    this.stopMonitoring();
  }

  /**
   * Register callback for warning events
   */
  onWarning(callback) {
    this.warningCallback = callback;
  }

  /**
   * Register callback for expiration events
   */
  onExpired(callback) {
    this.expiredCallback = callback;
  }

  /**
   * Register callback for session restored events (after signing in on another tab)
   */
  onSessionRestored(callback) {
    this.sessionRestoredCallback = callback;
  }

  /**
   * Stop all monitoring
   */
  stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
  }

  /**
   * Reset session timer (call this after successful login)
   */
  resetSession() {
    this.sessionStart = Date.now();
    this.lastActivity = Date.now();
    this.isWarningShown = false;
    this.isSessionExpired = false;
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
